import React, { useEffect, useState } from 'react';
import {
    Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    IconButton, TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import {
    ChangeSetRequestError, IngestionMetadata, MAX_UPLOAD_BYTES, startIngestion, uploadPolicyDocument,
} from '../../../api/ontologyChangeSetApi';
import { normalizeDocumentList, sourceDocumentsApi } from '../../../api/ontologyApi';
import { STATUS_LABEL } from './changeSetReview';
import {
    DIALOG_PAPER_SX, FIELD_SX, LABEL_SX, MONO_SX, Notice, PRIMARY_BUTTON_SX, SECONDARY_BUTTON_SX,
} from './changeSetUi';
import { dsVar } from '../../../theme/tokens';

type Stage = 'pick' | 'uploading' | 'details' | 'starting' | 'duplicate';

interface RegisteredDocument { code: string; title: string; versionLabel?: string }

export interface IngestDocumentDialogProps {
    open: boolean;
    onClose: () => void;
    /** The flow has started; it reaches review in a few minutes. */
    onStarted: (result: { uploadId: string; referenceId: string | null }) => void;
    /** The same bytes already have a change set: open it instead. */
    onOpenChangeSet: (changeSetId: string) => void;
}

const METADATA_FIELDS: Array<{ key: keyof IngestionMetadata; label: string; type?: string; multiline?: boolean }> = [
    { key: 'title', label: 'Title' },
    { key: 'versionLabel', label: 'Version' },
    { key: 'issuer', label: 'Issuer' },
    { key: 'documentIdentifier', label: 'Reference number' },
    { key: 'issuanceDate', label: 'Issued on', type: 'date' },
    { key: 'effectiveDate', label: 'Effective from', type: 'date' },
];

const isPdf = (file: File) => file.type === 'application/pdf' || /\.pdf$/i.test(file.name);

/**
 * Upload a policy document and start INGEST_POLICY_DOCUMENT_TO_ONTOLOGY.
 *
 * Two steps, because the file is checked for a duplicate as soon as it is stored: identical
 * bytes with a live change set would otherwise be skipped by the engine without a word.
 *
 *   pick → uploading → details → starting      (or → duplicate)
 *
 * Every metadata field is optional and OVERRIDES what the engine reads from the document,
 * so the form starts empty and only what the user types is sent.
 */
const IngestDocumentDialog: React.FC<IngestDocumentDialogProps> = ({ open, onClose, onStarted, onOpenChangeSet }) => {
    const [stage, setStage] = useState<Stage>('pick');
    const [file, setFile] = useState<File | null>(null);
    const [uploadId, setUploadId] = useState<string | null>(null);
    const [duplicate, setDuplicate] = useState<{ changeSetId: string; status?: string; title?: string | null } | null>(null);
    const [metadata, setMetadata] = useState<IngestionMetadata>({});
    const [revises, setRevises] = useState<RegisteredDocument | null>(null);
    const [error, setError] = useState<string | null>(null);

    // The "This revises…" picker. Never pre-filled: the engine trusts a sourceDocCode it is
    // sent without any check (spec §3.6).
    const [query, setQuery] = useState('');
    const [options, setOptions] = useState<RegisteredDocument[]>([]);
    const [optionsLoading, setOptionsLoading] = useState(false);

    useEffect(() => {
        if (!open) {
            setStage('pick');
            setFile(null);
            setUploadId(null);
            setDuplicate(null);
            setMetadata({});
            setRevises(null);
            setError(null);
            setQuery('');
        }
    }, [open]);

    useEffect(() => {
        if (!open || stage !== 'details') return undefined;
        const controller = new AbortController();
        const timer = setTimeout(async () => {
            setOptionsLoading(true);
            try {
                const res = await sourceDocumentsApi.list({ q: query || undefined, limit: 20 }, { signal: controller.signal });
                const { documents } = normalizeDocumentList(res);
                setOptions(documents.map((doc: Record<string, any>) => ({
                    code: doc.source_doc_code, title: doc.title || doc.source_doc_code, versionLabel: doc.version_label,
                })).filter((doc: RegisteredDocument) => Boolean(doc.code)));
            } catch {
                if (!controller.signal.aborted) setOptions([]);
            } finally {
                if (!controller.signal.aborted) setOptionsLoading(false);
            }
        }, 250);
        return () => { clearTimeout(timer); controller.abort(); };
    }, [open, stage, query]);

    const chooseFile = async (picked?: File | null) => {
        if (!picked) return;
        setError(null);
        if (!isPdf(picked)) {
            setError('Choose a PDF.');
            return;
        }
        if (picked.size > MAX_UPLOAD_BYTES) {
            setError(`The file is larger than ${Math.round(MAX_UPLOAD_BYTES / (1024 * 1024))} MB.`);
            return;
        }
        setFile(picked);
        setStage('uploading');
        try {
            const outcome = await uploadPolicyDocument(picked);
            if (outcome.kind === 'duplicate') {
                setDuplicate({ changeSetId: outcome.duplicateOf, status: outcome.changeSetStatus, title: outcome.title });
                setStage('duplicate');
                return;
            }
            setUploadId(outcome.uploadId);
            setStage('details');
        } catch (err) {
            setError(err instanceof ChangeSetRequestError ? err.message : 'The document could not be uploaded.');
            setStage('pick');
        }
    };

    const start = async () => {
        if (!uploadId) return;
        setStage('starting');
        setError(null);
        try {
            const result = await startIngestion(uploadId, { ...metadata, sourceDocCode: revises?.code });
            onStarted(result);
        } catch (err) {
            setError(err instanceof ChangeSetRequestError ? err.message : 'The document could not be submitted.');
            setStage('details');
        }
    };

    const busy = stage === 'uploading' || stage === 'starting';

    return (
        <Dialog
            open={open}
            onClose={busy ? undefined : onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: DIALOG_PAPER_SX }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '1rem', fontWeight: 600, color: dsVar('fg') }}>
                Upload a policy document
                <IconButton onClick={onClose} disabled={busy} size="small" sx={{ color: dsVar('fg-2') }} aria-label="Close">
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-2'), lineHeight: 1.6, mb: 2 }}>
                    The document is read for the concepts, policies and thresholds it sets out, and compared with
                    what is in force. Nothing changes in the library until you review and approve the result.
                </Typography>

                {(stage === 'pick' || stage === 'uploading') && (
                    <Box sx={{ p: 2.5, borderRadius: '10px', border: `1px dashed ${dsVar('line')}`, textAlign: 'center' }}>
                        {stage === 'uploading' ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                                <CircularProgress size={18} sx={{ color: dsVar('accent-fg') }} />
                                <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-soft') }}>Uploading {file?.name}…</Typography>
                            </Box>
                        ) : (
                            <>
                                <Button
                                    component="label"
                                    variant="outlined"
                                    startIcon={<UploadFileOutlinedIcon />}
                                    sx={{ textTransform: 'none', color: dsVar('accent-fg'), borderColor: 'rgba(255, 222, 89, 0.5)', '&:hover': { borderColor: dsVar('accent-fg') } }}
                                >
                                    Choose PDF
                                    <input
                                        hidden
                                        type="file"
                                        accept="application/pdf,.pdf"
                                        data-testid="policy-file-input"
                                        onChange={(e) => {
                                            chooseFile(e.target.files?.[0]);
                                            e.target.value = '';
                                        }}
                                    />
                                </Button>
                                <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted'), mt: 1 }}>
                                    Your organisation's own policy, up to {Math.round(MAX_UPLOAD_BYTES / (1024 * 1024))} MB.
                                </Typography>
                            </>
                        )}
                    </Box>
                )}

                {stage === 'duplicate' && duplicate && (
                    <Notice tone="info">
                        <strong>This document has already been uploaded.</strong>{' '}
                        {duplicate.title ? `"${duplicate.title}" ` : 'Its change set '}
                        is {STATUS_LABEL[duplicate.status as keyof typeof STATUS_LABEL]?.toLowerCase() ?? duplicate.status}.
                        To upload the same file again, that update must first be rejected.
                    </Notice>
                )}

                {(stage === 'details' || stage === 'starting') && (
                    <Box>
                        <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-body'), mb: 2 }}>
                            <Box component="span" sx={MONO_SX}>{file?.name}</Box> uploaded.
                        </Typography>

                        <Typography sx={LABEL_SX}>Details (optional)</Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted'), mb: 1.5 }}>
                            Leave blank to read each one from the document. Anything you enter replaces what the document says.
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mb: 2 }}>
                            {METADATA_FIELDS.map((field) => (
                                <TextField
                                    key={field.key}
                                    label={field.label}
                                    size="small"
                                    type={field.type}
                                    value={metadata[field.key] ?? ''}
                                    onChange={(e) => setMetadata((current) => ({ ...current, [field.key]: e.target.value }))}
                                    disabled={stage === 'starting'}
                                    InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
                                    sx={FIELD_SX}
                                />
                            ))}
                        </Box>

                        <Typography sx={LABEL_SX}>This revises… (optional)</Typography>
                        <Autocomplete
                            size="small"
                            options={options}
                            value={revises}
                            loading={optionsLoading}
                            disabled={stage === 'starting'}
                            onChange={(_e, value) => setRevises(value)}
                            onInputChange={(_e, value, reason) => { if (reason === 'input') setQuery(value); }}
                            isOptionEqualToValue={(a, b) => a.code === b.code}
                            getOptionLabel={(doc) => `${doc.title}${doc.versionLabel ? ` (v${doc.versionLabel})` : ''}`}
                            filterOptions={(x) => x}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Pick only if this is a new version of a registered document"
                                    helperText="Left empty, the document is matched by its reference number or title."
                                    sx={FIELD_SX}
                                />
                            )}
                        />

                        <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted'), mt: 2 }}>
                            Reading the document takes a few minutes. It will appear under Policy Updates, ready for review.
                        </Typography>
                    </Box>
                )}

                {error && <Box sx={{ mt: 2 }}><Notice tone="error">{error}</Notice></Box>}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={onClose} disabled={busy} sx={SECONDARY_BUTTON_SX}>
                    {stage === 'duplicate' ? 'Close' : 'Cancel'}
                </Button>
                {stage === 'duplicate' && duplicate && (
                    <Button onClick={() => onOpenChangeSet(duplicate.changeSetId)} sx={PRIMARY_BUTTON_SX}>
                        Open change set
                    </Button>
                )}
                {(stage === 'details' || stage === 'starting') && (
                    <Button onClick={start} disabled={stage === 'starting'} sx={PRIMARY_BUTTON_SX}>
                        {stage === 'starting' ? 'Submitting…' : 'Submit for review'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default IngestDocumentDialog;
